import { useState, useRef, useEffect, useCallback } from "react";
import {
  subscribeToMeTopic,
  getTopics,
  TINODE_CONTACT_LIMIT,
  generateTinodeSecret,
  getStoredAuthToken,
  storeAuthToken,
  clearAuthToken,
} from "../services/tinodeApi";
import {
  getTinodeInstance,
  connectTinodeInstance,
} from "../services/tinodeSingleton";
import {
  parseTinodeError,
  isNonFatalTinodeError,
} from "../utils/tinodeErrorHelper";
import { SESSION_END_MARKER } from "../services/tinodeSessionService";

const MAX_RETRY = 6;
const RETRY_DELAYS = [1000, 2000, 3000, 5000, 8000, 12000];

const getTopicName = (topic) =>
  typeof topic === "string" ? topic : topic?.topic || topic?.id || topic?.name;

const sortTopicsByActivity = (items) => {
  const now = Date.now();
  items.sort((a, b) => {
    const aTime = a.touched
      ? new Date(a.touched).getTime()
      : a.updated
        ? new Date(a.updated).getTime()
        : now;
    const bTime = b.touched
      ? new Date(b.touched).getTime()
      : b.updated
        ? new Date(b.updated).getTime()
        : now;
    return bTime - aTime;
  });
  return items;
};

const getPeerNameFromTopic = (tinode, topicObj) => {
  if (!tinode || !topicObj) return null;
  try {
    const topicName =
      typeof topicObj === "string"
        ? topicObj
        : topicObj.topic || topicObj.id || topicObj.name || "";

    const topic = tinode.getTopic(topicName);
    if (!topic) return null;

    const currentUserId = tinode.getCurrentUserID();
    let peerName = null;

    topic.subscribers((sub) => {
      if (sub.user && sub.user !== currentUserId && !peerName) {
        peerName = sub.public?.fn || sub.public?.n || null;
      }
    });

    return peerName;
  } catch (_) {
    return null;
  }
};

const getLastMessageText = (tinode, topicObj) => {
  if (!tinode || !topicObj) return null;
  try {
    const topicName =
      typeof topicObj === "string"
        ? topicObj
        : topicObj.topic || topicObj.id || topicObj.name || "";

    const topic = tinode.getTopic(topicName);
    if (!topic) return null;

    let lastMsg = null;
    let lastSeq = -1;

    topic.allMessages((msg) => {
      if (!msg?.seq) return;
      if (msg.seq <= lastSeq) return;

      const content = msg.content;
      const text = typeof content === "string" ? content : content?.txt || "";

      if (text.includes(SESSION_END_MARKER) || text.includes("__system__"))
        return;

      lastSeq = msg.seq;
      lastMsg = msg;
    });

    if (!lastMsg) return null;

    const content = lastMsg.content;

    // Pesan teks biasa
    if (typeof content === "string") {
      return content.length > 60 ? content.substring(0, 60) + "..." : content;
    }

    // Pesan Drafty (dengan format/entitas)
    if (content?.txt) {
      const txt = content.txt;
      return txt.length > 60 ? txt.substring(0, 60) + "..." : txt;
    }

    // Pesan gambar (ent dengan tp="IM")
    if (content?.ent?.some((e) => e.tp === "IM")) {
      return "📷 Gambar";
    }

    return null;
  } catch (_) {
    return null;
  }
};

/**
 * Enrich topic object dengan peerName dan lastMessage.
 * Dipanggil setiap kali topics di-refresh.
 */
const enrichTopics = (rawTopics, tinode) => {
  if (!tinode) return rawTopics;

  return rawTopics.map((t) => {
    const peerName = getPeerNameFromTopic(tinode, t);
    const lastMessage = getLastMessageText(tinode, t);

    return {
      ...t,
      // Nama lawan bicara (bukan nama group)
      peerName: peerName || null,
      // Nama yang ditampilkan di UI: prioritas peerName → nama group → topicId
      displayName:
        peerName ||
        (typeof t === "object" ? t.public?.fn || t.name || "" : t) ||
        (typeof t === "string" ? t : t.topic || t.id || ""),
      // Teks pesan terakhir
      lastMessage: lastMessage || null,
    };
  });
};

export const useTinodeConnection = (user) => {
  const tinodeRef = useRef(null);
  const isInitializingRef = useRef(false);
  const retryCountRef = useRef(0);
  const retryTimerRef = useRef(null);
  const isMountedRef = useRef(true);
  const meTopicRef = useRef(null);
  const isLoggingInRef = useRef(false);
  const isSubscribingMeRef = useRef(false);
  const meSubscribeRetryDoneRef = useRef(false);
  const refreshTopicsRef = useRef(null);
  const forceRefreshInProgressRef = useRef(false);
  const pendingForceRefreshRef = useRef(false);
  const lastTopicCountLogRef = useRef(null);

  const [isConnected, setIsConnected] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [topics, setTopics] = useState([]);
  const [userInfo, setUserInfo] = useState(null);

  /**
   * Set topics dengan enrichment (peerName + lastMessage).
   * Semua pemanggilan setTopics harus lewat sini.
   */
  const setEnrichedTopics = useCallback((rawList, tinode) => {
    const enriched = enrichTopics(rawList, tinode || tinodeRef.current);
    setTopics(sortTopicsByActivity(enriched));
  }, []);

  const mergeEnrichedTopics = useCallback((rawList, tinode, options = {}) => {
    const { replace = false, deletedTopicNames = [] } = options;
    const enriched = enrichTopics(rawList, tinode || tinodeRef.current);
    const deletedSet = new Set(deletedTopicNames.filter(Boolean));

    setTopics((prev) => {
      if (replace) {
        return sortTopicsByActivity(
          enriched.filter((topic) => !deletedSet.has(getTopicName(topic))),
        );
      }

      const byName = new Map();
      prev.forEach((topic) => {
        const name = getTopicName(topic);
        if (name && !deletedSet.has(name)) {
          byName.set(name, topic);
        }
      });
      enriched.forEach((topic) => {
        const name = getTopicName(topic);
        if (name && !deletedSet.has(name)) {
          byName.set(name, topic);
        }
      });

      return sortTopicsByActivity(Array.from(byName.values()));
    });
  }, []);

  const getVisibleTopics = useCallback((tinode) => {
    const list = getTopics(tinode).filter((t) => {
      const name = getTopicName(t);
      return name && name !== "me" && !name.startsWith("fnd");
    });

    if (lastTopicCountLogRef.current !== list.length) {
      lastTopicCountLogRef.current = list.length;
      console.info("[Tinode] doctor topic cache count", {
        count: list.length,
      });
    }

    return list;
  }, []);

  const refreshTopics = useCallback(
    (tinode, options = {}) => {
      if (refreshTopicsRef.current) clearTimeout(refreshTopicsRef.current);
      refreshTopicsRef.current = setTimeout(() => {
        if (!isMountedRef.current || !tinode) return;
        mergeEnrichedTopics(getVisibleTopics(tinode), tinode, options);
      }, 80);
    },
    [getVisibleTopics, mergeEnrichedTopics],
  );

  const forceRefreshTopics = useCallback(
    async (tinode) => {
      if (!tinode || !isMountedRef.current) return;
      if (forceRefreshInProgressRef.current) {
        pendingForceRefreshRef.current = true;
        return;
      }
      forceRefreshInProgressRef.current = true;
      let fullSyncSucceeded = false;

      try {
        const me = meTopicRef.current || tinode.getMeTopic();
        if (me && me.isSubscribed()) {
          await me.getMeta(
            me
              .startMetaQuery()
              .withSub(undefined, TINODE_CONTACT_LIMIT)
              .build(),
          );
          fullSyncSucceeded = true;
          console.info("[Tinode] full contact refresh completed", {
            limit: TINODE_CONTACT_LIMIT,
          });
          await new Promise((r) => setTimeout(r, 250));
        }
      } catch (err) {
        console.warn("[Tinode] full contact refresh failed", {
          code: err?.code,
          text: err?.text || err?.message || parseTinodeError(err),
        });
      }

      if (isMountedRef.current && tinode) {
        mergeEnrichedTopics(getVisibleTopics(tinode), tinode, {
          replace: fullSyncSucceeded,
        });
      }

      forceRefreshInProgressRef.current = false;

      if (pendingForceRefreshRef.current && isMountedRef.current && tinode) {
        pendingForceRefreshRef.current = false;
        mergeEnrichedTopics(getVisibleTopics(tinode), tinode, { replace: false });
      }
    },
    [getVisibleTopics, mergeEnrichedTopics],
  );

  const attemptLogin = useCallback(
    async (tinode) => {
      if (tinode.isAuthenticated()) return true;

      if (isLoggingInRef.current) {
        let waitCount = 0;
        while (isLoggingInRef.current && waitCount < 50) {
          await new Promise((r) => setTimeout(r, 100));
          waitCount++;
          if (tinode.isAuthenticated()) return true;
        }
        if (tinode.isAuthenticated()) return true;
      }

      isLoggingInRef.current = true;

      try {
        const storedToken = getStoredAuthToken();
        if (storedToken?.token) {
          try {
            await tinode.loginToken(storedToken.token);
            const tok = tinode.getAuthToken();
            if (tok?.token) storeAuthToken(tok);
            isLoggingInRef.current = false;
            return true;
          } catch (err) {
            const msg = parseTinodeError(err);
            if (err?.code === 409 || msg?.includes("already authenticated")) {
              isLoggingInRef.current = false;
              return true;
            }
            clearAuthToken();
          }
        }

        const password = sessionStorage.getItem("tinode_password");
        if (!password) {
          isLoggingInRef.current = false;
          throw new Error("Session habis. Silakan login kembali.");
        }

        const email = user?.email || localStorage.getItem("email");
        if (!email) {
          isLoggingInRef.current = false;
          throw new Error("Email tidak ditemukan. Silakan login kembali.");
        }

        const secret = generateTinodeSecret(email, password);
        try {
          await tinode.login("basic", secret, null);
          const tok = tinode.getAuthToken();
          if (tok?.token) storeAuthToken(tok);
          isLoggingInRef.current = false;
          return true;
        } catch (err) {
          const msg = parseTinodeError(err);
          if (err?.code === 409 || msg?.includes("already authenticated")) {
            isLoggingInRef.current = false;
            return true;
          }
          isLoggingInRef.current = false;
          throw new Error(msg || "Login gagal.");
        }
      } catch (err) {
        isLoggingInRef.current = false;
        throw err;
      }
    },
    [user?.email],
  );

  const scheduleRetry = useCallback((reason) => {
    if (!isMountedRef.current) return;
    if (retryCountRef.current >= MAX_RETRY) {
      if (isMountedRef.current) {
        setError(
          "Tidak dapat terhubung ke server chat. Periksa koneksi internet Anda, lalu refresh halaman.",
        );
        setIsLoading(false);
      }
      return;
    }

    const delay = RETRY_DELAYS[retryCountRef.current] || 12000;
    retryCountRef.current += 1;

    if (process.env.NODE_ENV === "development") {
      console.debug(
        `[Tinode] Retry ${retryCountRef.current}/${MAX_RETRY} in ${delay}ms — ${reason}`,
      );
    }

    retryTimerRef.current = setTimeout(() => {
      if (isMountedRef.current) {
        isInitializingRef.current = false;
        initializeTinode();
      }
    }, delay);
  }, []);

  const initializeTinode = useCallback(async () => {
    if (!user || !isMountedRef.current) return;
    if (isInitializingRef.current) return;

    isInitializingRef.current = true;
    meSubscribeRetryDoneRef.current = false;

    try {
      const tinode = await getTinodeInstance();
      if (!isMountedRef.current) {
        isInitializingRef.current = false;
        return;
      }

      tinodeRef.current = tinode;

      tinode.onConnect = () => {
        if (!isMountedRef.current) return;
        setIsConnected(true);
        setError(null);
      };

      tinode.onDisconnect = () => {
        if (!isMountedRef.current) return;
        setIsConnected(false);
        setIsReady(false);
      };

      tinode.onError = (err) => {
        if (isNonFatalTinodeError(err)) return;
        if (err instanceof Event || err?.isTrusted !== undefined) return;
        if (err?.type === "error" || err?.type === "close") return;
        if (process.env.NODE_ENV === "development") {
          console.warn("[Tinode] onError:", parseTinodeError(err));
        }
      };

      if (!tinode.isConnected()) {
        try {
          await connectTinodeInstance(tinode, 15000);
          await new Promise((r) => setTimeout(r, 300));
        } catch (connectErr) {
          isInitializingRef.current = false;
          scheduleRetry(parseTinodeError(connectErr) || "connect failed");
          return;
        }
      }

      if (!isMountedRef.current) {
        isInitializingRef.current = false;
        return;
      }

      try {
        await attemptLogin(tinode);
      } catch (loginErr) {
        const msg = parseTinodeError(loginErr);
        const isCredential =
          msg?.includes("Session habis") || msg?.includes("Silakan login");
        isInitializingRef.current = false;
        if (isCredential) {
          setError(msg);
          setIsLoading(false);
        } else scheduleRetry(msg || "login failed");
        return;
      }

      if (!isMountedRef.current) {
        isInitializingRef.current = false;
        return;
      }
      if (!tinode.isAuthenticated()) {
        isInitializingRef.current = false;
        scheduleRetry("authenticated check failed");
        return;
      }

      if (!isSubscribingMeRef.current) {
        isSubscribingMeRef.current = true;
        try {
          const me = await subscribeToMeTopic(tinode);
          meTopicRef.current = me;
          isSubscribingMeRef.current = false;
        } catch (err) {
          isSubscribingMeRef.current = false;
          const msg = parseTinodeError(err);
          const isAuthError =
            err?.code === 401 ||
            err?.text === "authentication required" ||
            msg?.includes("authentication required");

          if (isAuthError) {
            // Log: 401 padahal isAuthenticated true → server menolak subscribe "me".
            // Paksa satu kali basic login (clear token) lalu retry subscribe; batasi satu retry.
            if (!meSubscribeRetryDoneRef.current) {
              meSubscribeRetryDoneRef.current = true;
              clearAuthToken();
              try {
                await attemptLogin(tinode);
                if (!tinode.isAuthenticated()) {
                  throw new Error(
                    msg || "Authentication required when subscribing to 'me' topic.",
                  );
                }
                const me = await subscribeToMeTopic(tinode);
                meTopicRef.current = me;
              } catch (authErr) {
                const authMsg = parseTinodeError(authErr);
                const isCredential =
                  authMsg?.includes("Session habis") ||
                  authMsg?.includes("Silakan login");
                isInitializingRef.current = false;
                if (isCredential) {
                  setError(
                    authMsg ||
                      "Sesi chat berakhir. Silakan login kembali untuk melanjutkan.",
                  );
                  setIsLoading(false);
                } else {
                  setError(
                    "Tidak dapat memuat data chat. Silakan refresh halaman atau login ulang.",
                  );
                  setIsLoading(false);
                }
                return;
              }
            } else {
              isInitializingRef.current = false;
              setError(
                "Tidak dapat memuat data chat. Silakan refresh halaman atau login ulang.",
              );
              setIsLoading(false);
              return;
            }
          } else if (err?.code === 404) {
            // 404: topic "me" mungkin belum siap di server. Satu kali retry subscribe.
            if (!meSubscribeRetryDoneRef.current) {
              meSubscribeRetryDoneRef.current = true;
              try {
                const me = await subscribeToMeTopic(tinode);
                meTopicRef.current = me;
              } catch (retryErr) {
                isInitializingRef.current = false;
                scheduleRetry(
                  parseTinodeError(retryErr) || "Subscribe topic 'me' gagal (404).",
                );
                return;
              }
            } else {
              isInitializingRef.current = false;
              scheduleRetry("Subscribe topic 'me' gagal (404).");
              return;
            }
          } else {
            throw err;
          }
        }
      } else {
        let waitCount = 0;
        while (isSubscribingMeRef.current && waitCount < 30) {
          await new Promise((r) => setTimeout(r, 100));
          waitCount++;
        }
        const me = tinode.getMeTopic();
        if (me && me.isSubscribed()) {
          meTopicRef.current = me;
        }
      }

      const me = meTopicRef.current || tinode.getMeTopic();

      me.onMetaDesc = (desc) => {
        if (!desc || !isMountedRef.current) return;
        const pub = desc.public || {};
        setUserInfo({
          fullname: pub.fn || user?.fullname,
          created: desc.created,
          updated: desc.updated,
          tags: me.tags() || [],
        });
      };

      me.onMeta = (meta) => {
        if (!isMountedRef.current) return;
        if (meta?.sub && Array.isArray(meta.sub) && meta.sub.length > 0) {
          const deletedTopicNames = meta.sub
            .filter((sub) => sub?.deleted)
            .map((sub) => sub.topic);
          refreshTopics(tinode, { deletedTopicNames });
        }
      };

      me.onPres = (pres) => {
        if (!isMountedRef.current) return;
        const triggerRefresh = ["msg", "recv", "acs", "upd", "on", "off", "ua"];
        if (pres?.topic === "me" && triggerRefresh.includes(pres?.what)) {
          if (["acs", "msg", "recv", "on"].includes(pres.what)) {
            forceRefreshTopics(tinode);
            // Dispatch event saat ada topic baru / akses berubah (group baru dari customer)
            if (["acs", "msg"].includes(pres.what) && pres.src && typeof window !== "undefined") {
              window.dispatchEvent(
                new CustomEvent("tinode-new-topic", {
                  detail: { topicName: pres.src, what: pres.what },
                }),
              );
            }
          } else {
            refreshTopics(tinode);
          }
        }
      };

      me.onContactUpdate = (what, cont) => {
        if (!isMountedRef.current) return;
        if (["msg", "recv", "acs", "upd"].includes(what)) {
          const deletedTopicNames = cont?.deleted && cont?.topic ? [cont.topic] : [];
          if (["acs", "msg", "recv"].includes(what)) {
            forceRefreshTopics(tinode);
            // Dispatch event saat ada contact update untuk topic baru
            if (["acs", "msg"].includes(what) && cont?.topic && typeof window !== "undefined") {
              window.dispatchEvent(
                new CustomEvent("tinode-new-topic", {
                  detail: { topicName: cont.topic, what },
                }),
              );
            }
          } else {
            refreshTopics(tinode, { deletedTopicNames });
          }
        }
      };

      me.onSubsUpdated = () => {
        if (isMountedRef.current) {
          forceRefreshTopics(tinode);
        }
      };

      await new Promise((r) => setTimeout(r, 400));
      refreshTopics(tinode, { replace: true });

      retryCountRef.current = 0;

      if (isMountedRef.current) {
        setIsConnected(true);
        setIsLoading(false);
        setError(null);
        setIsReady(true);
      }
    } catch (err) {
      if (!isMountedRef.current) {
        isInitializingRef.current = false;
        return;
      }

      const errMsg = parseTinodeError(err);
      const isCredential =
        errMsg?.includes("Session habis") || errMsg?.includes("Silakan login");

      isInitializingRef.current = false;

      if (isCredential) {
        setError(errMsg);
        setIsLoading(false);
      } else scheduleRetry(errMsg || "unknown");
    } finally {
      isInitializingRef.current = false;
    }
  }, [
    user?.email,
    user?.fullname,
    refreshTopics,
    forceRefreshTopics,
    attemptLogin,
    scheduleRetry,
  ]);

  useEffect(() => {
    if (!user) {
      setIsLoading(false);
      return;
    }

    isMountedRef.current = true;
    retryCountRef.current = 0;

    if (
      tinodeRef.current?.isConnected() &&
      tinodeRef.current?.isAuthenticated()
    ) {
      const tinode = tinodeRef.current;
      setIsConnected(true);

      if (!isSubscribingMeRef.current) {
        isSubscribingMeRef.current = true;
        subscribeToMeTopic(tinode)
          .then((me) => {
            meTopicRef.current = me;
            isSubscribingMeRef.current = false;
            refreshTopics(tinode, { replace: true });
            if (isMountedRef.current) {
              setIsReady(true);
              setIsLoading(false);
            }
          })
          .catch(() => {
            isSubscribingMeRef.current = false;
            if (isMountedRef.current) {
              setIsReady(true);
              setIsLoading(false);
            }
          });
      } else {
        const me = tinode.getMeTopic();
        if (me && me.isSubscribed()) {
          meTopicRef.current = me;
        }
        refreshTopics(tinode, { replace: true });
        if (isMountedRef.current) {
          setIsReady(true);
          setIsLoading(false);
        }
      }
      return;
    }

    initializeTinode();

    return () => {
      isMountedRef.current = false;
      isInitializingRef.current = false;
      if (retryTimerRef.current) {
        clearTimeout(retryTimerRef.current);
        retryTimerRef.current = null;
      }
      if (refreshTopicsRef.current) clearTimeout(refreshTopicsRef.current);
    };
  }, [user?.email, user?.fullname]);

  const setTopicsEnriched = useCallback(
    (listOrFn) => {
      if (typeof listOrFn === "function") {
        setTopics((prev) => {
          const next = listOrFn(prev);
          return enrichTopics(next, tinodeRef.current);
        });
      } else {
        setEnrichedTopics(listOrFn, tinodeRef.current);
      }
    },
    [setEnrichedTopics],
  );

  const retryConnection = useCallback(() => {
    if (isInitializingRef.current) return; // Prevent multiple simultaneous retries
    if (retryTimerRef.current) {
      clearTimeout(retryTimerRef.current);
      retryTimerRef.current = null;
    }
    retryCountRef.current = 0; // Reset retry counter
    setError(null); // Clear error
    setIsLoading(true); // Set loading
    initializeTinode(); // Retry initialization
  }, [initializeTinode]);

  return {
    tinodeRef,
    isConnected,
    isReady,
    isLoading,
    error,
    topics,
    setTopics: setTopicsEnriched,
    userInfo,
    clearAuthToken,
    retryConnection,
  };
};
